import React, { useState, useRef, useEffect } from 'react';
import { trackEvent } from '../../utils/analytics';
import '../../styles/components/Projects.css';
import CaseStudies from '../projects/CaseStudies';
import VibeCodingExperiments from '../projects/VibeCodingExperiments';
import Articles from '../projects/Articles';
import Timeline from '../projects/Timeline';
import Testimonials from '../projects/Testimonials';

// Human-readable labels for each tab id. Used as the `tab_label` param
// on the tab_changed GA event so reports show "AI Experiments"
// instead of "vibe-coding". (Tab id stays 'vibe-coding' so existing
// GA data, deep links, and code references aren't disrupted by a
// label-only rename.)
const TAB_LABELS = {
  'case-studies': 'Case Studies',
  'vibe-coding': 'AI Experiments',
  'articles': 'Articles',
  'timeline': 'Timeline',
  'testimonials': 'Testimonials',
};

const Projects = () => {
  const [activeTab, setActiveTab] = useState('case-studies');

  // Wraps setActiveTab to fire a GA event on each user-initiated change.
  // Skips firing if the visitor clicks the tab they're already on, and
  // doesn't fire for the default 'case-studies' on initial mount — that
  // bucket is captured by the home-page page_view.
  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    trackEvent('tab_changed', {
      tab_id: tabId,
      tab_label: TAB_LABELS[tabId] || tabId,
    });
  };
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const tabsContainerRef = useRef(null);

  const checkScrollPosition = () => {
    if (tabsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, []);

  return (
    <div className="Projects">
      <div className="tabs-wrapper">
        <div className="tabs-container" ref={tabsContainerRef} onScroll={checkScrollPosition}>
          <button 
            className={`tab-button ${activeTab === 'case-studies' ? 'active' : ''}`}
            onClick={() => handleTabChange('case-studies')}
          >
            Case Studies
          </button>
          <button
            className={`tab-button shimmer-tab ${activeTab === 'vibe-coding' ? 'active' : ''}`}
            onClick={() => handleTabChange('vibe-coding')}
          >
            AI Experiments
          </button>
          <button 
            className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
            onClick={() => handleTabChange('articles')}
          >
            Articles
          </button>
          <button 
            className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => handleTabChange('timeline')}
          >
            Timeline
          </button>
          <button 
            className={`tab-button ${activeTab === 'testimonials' ? 'active' : ''}`}
            onClick={() => handleTabChange('testimonials')}
          >
            Testimonials
          </button>
        </div>
        {showLeftScroll && <div className="scroll-indicator left" onClick={scrollLeft}>‹</div>}
        {showRightScroll && <div className="scroll-indicator right" onClick={scrollRight}>›</div>}
      </div>

      {activeTab === 'case-studies' && <CaseStudies />}
      {activeTab === 'vibe-coding' && <VibeCodingExperiments />}
      {activeTab === 'articles' && <Articles />}
      {activeTab === 'timeline' && <Timeline />}
      {activeTab === 'testimonials' && <Testimonials />}
    </div>
  );
};

export default Projects;
